/**
 * Mutu Buah Filter Component
 * Provides estate, division, and date selection for fruit quality inspection reports
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Input,
    Button,
    Select,
    Flex,
    Heading,
    Grid,
    useBreakpointValue,
    useTheme,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import api from '../../../auth/apiClient';
import { MutuBuahFilters, Estate, Division } from '../types';

interface FilterMutuBuahProps {
    filters: MutuBuahFilters;
    onFilterChange: (filters: MutuBuahFilters) => void;
    onClearFilters: () => void;
    onApplyFilters: () => void;
}

const FilterMutuBuah: React.FC<FilterMutuBuahProps> = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
}) => {
    const theme = useTheme();
    const [localFilters, setLocalFilters] = useState<MutuBuahFilters>(filters);
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const footerGap = useBreakpointValue({ base: 2, md: 3 });

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Style constants matching other filters
    const inputStyles = {
        size: "sm" as const,
        borderRadius: "lg",
        borderColor: "gray.200",
        _hover: { borderColor: "gray.300" },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    // Fetch estates on component mount
    useEffect(() => {
        const fetchEstates = async () => {
            try {
                const response = await api.get('/inspeksi/web/estate-db');
                setEstates(response.data);
            } catch (error) {
                console.error("Error fetching estates:", error);
            }
        };
        fetchEstates();
    }, []);

    // Fetch divisions when estate changes
    useEffect(() => {
        const fetchDivisions = async () => {
            if (!localFilters.estate_id) {
                setDivisions([]);
                return;
            }

            try {
                const response = await api.get('/inspeksi/options');
                const allDivisions = response.data.divisis || [];
                const filteredDivisions = allDivisions.filter(
                    (div: Division) => div.estate_id === localFilters.estate_id
                );
                setDivisions(filteredDivisions);
            } catch (error) {
                console.error("Error fetching divisions:", error);
            }
        };
        fetchDivisions();
    }, [localFilters.estate_id]);

    const handleEstateChange = useCallback((value: string) => {
        const estateId = value ? parseInt(value) : null;
        const newFilters = {
            ...localFilters,
            estate_id: estateId,
            division_id: null // Reset division when estate changes
        };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    }, [localFilters, onFilterChange]);

    const handleDivisionChange = useCallback((value: string) => {
        const divisionId = value ? parseInt(value) : null;
        const newFilters = { ...localFilters, division_id: divisionId };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    }, [localFilters, onFilterChange]);

    const handleDateChange = useCallback((value: string) => {
        const newFilters = { ...localFilters, inspection_date: value };
        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    }, [localFilters, onFilterChange]);

    const handleApplyFilters = useCallback(() => {
        onFilterChange(localFilters);
        onApplyFilters();
    }, [localFilters, onFilterChange, onApplyFilters]);

    const handleClearFilters = useCallback(() => {
        const clearedFilters: MutuBuahFilters = {
            estate_id: null,
            division_id: null,
            inspection_date: getTodayDate(),
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClearFilters();
    }, [onFilterChange, onClearFilters]);

    // Available divisions filtered by selected estate
    const availableDivisions = localFilters.estate_id
        ? divisions.filter(div => div.estate_id === localFilters.estate_id)
        : [];

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            bg="white"
        >
            {/* Header Section */}
            <Flex
                as="header"
                position="sticky"
                top={0}
                bg="white"
                zIndex={2}
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 2, md: 0 })}
                align="center"
                minHeight={useBreakpointValue({ base: "60px", md: "72px" })}
            >
                <Heading size={useBreakpointValue({ base: "sm", md: "md" })}>
                    Mutu Buah
                </Heading>
            </Flex>

            {/* Content Section - Scrollable */}
            <Box
                flex="1"
                overflowY="auto"
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 2, md: 3 })}
            >
                <Grid
                    templateColumns="1fr"
                    gap={useBreakpointValue({ base: 3, md: 4 })}
                >
                    {/* Estate Selection */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
                            Estate
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Pilih Estate"
                            value={localFilters.estate_id || ''}
                            onChange={(e) => handleEstateChange(e.target.value)}
                        >
                            {estates.map((estate) => (
                                <option key={estate.id} value={estate.id}>
                                    {estate.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Division Selection */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
                            Divisi
                        </FormLabel>
                        <Select
                            {...inputStyles}
                            placeholder="Pilih Divisi"
                            value={localFilters.division_id || ''}
                            onChange={(e) => handleDivisionChange(e.target.value)}
                            isDisabled={!localFilters.estate_id}
                        >
                            {availableDivisions.map((division) => (
                                <option key={division.id} value={division.id}>
                                    {division.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Inspection Date */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
                            Tanggal Inspeksi
                        </FormLabel>
                        <Input
                            {...inputStyles}
                            type="date"
                            value={localFilters.inspection_date}
                            onChange={(e) => handleDateChange(e.target.value)}
                        />
                    </FormControl>
                </Grid>
            </Box>

            {/* Footer Actions */}
            <Box
                as="footer"
                position="sticky"
                bottom={0}
                bg="white"
                px={useBreakpointValue({ base: 3, md: 4 })}
                py={useBreakpointValue({ base: 3, md: 4 })}
                borderTop="1px solid"
                borderColor="gray.200"
                zIndex={2}
            >
                <Flex gap={footerGap} direction="column">
                    <Button
                        leftIcon={<FiSearch />}
                        colorScheme="blue"
                        size="sm"
                        borderRadius="lg"
                        onClick={handleApplyFilters}
                        isDisabled={!localFilters.estate_id || !localFilters.division_id || !localFilters.inspection_date}
                    >
                        Terapkan Filter
                    </Button>
                    <Button
                        leftIcon={<MdClear />}
                        variant="outline"
                        colorScheme="gray"
                        size="sm"
                        borderRadius="lg"
                        onClick={handleClearFilters}
                    >
                        Bersihkan Filter
                    </Button>
                </Flex>
            </Box>
        </Box>
    );
};

export default FilterMutuBuah;
